import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadPDF, printThermal } from "../utils/exportHelpers";
import { inventoryApi } from "../utils/inventoryApi";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const InventoryLowStockReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [report, setReport] = useState({ items: [], total_items: 0 });
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const data = await inventoryApi.getLowStockReport({
        restaurantid: userId,
      });
      setReport({
        items: Array.isArray(data?.items) ? data.items : [],
        total_items: Number(data?.total_items || 0),
      });
    } catch (e) {
      toast.error(e.message || "Unable to load low stock report");
      setReport({ items: [], total_items: 0 });
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtMoney = (n) =>
    `Rs ${Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const totalValue = useMemo(
    () => report.items.reduce((sum, row) => sum + Number(row.stock_value || 0), 0),
    [report.items]
  );

  const exportCsv = () => {
    const rows = report.items.map((row, index) => [
      index + 1,
      row.name || "-",
      row.base_uom || "",
      row.available ?? 0,
      row.min_stock ?? 0,
      row.reorder_qty ?? 0,
      row.stock_value ?? 0,
    ]);
    downloadCSV(
      "inventory_low_stock.csv",
      ["#", "Item", "UOM", "Available", "Min Stock", "Reorder Qty", "Stock Value"],
      rows
    );
  };

  const exportPdf = () => downloadPDF(reportRef.current, "inventory_low_stock.pdf");

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Inventory Low Stock</Title>
          <Sub>Items that are below their minimum stock threshold.</Sub>
          <PillRow>
            <Pill>Items: {report.total_items}</Pill>
            <Pill>Total Stock Value: {fmtMoney(totalValue)}</Pill>
          </PillRow>
        </div>
        <Actions>
          <PrimaryBtn type="button" onClick={() => fetchReport({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </Top>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading report...</Center>
        ) : report.items.length === 0 ? (
          <Center>No low stock items found.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th>UOM</th>
                  <th>Available</th>
                  <th>Min Stock</th>
                  <th>Reorder Qty</th>
                  <th>Stock Value</th>
                </tr>
              </thead>
              <tbody>
                {report.items.map((item, index) => (
                  <tr key={`${item.id || item.name || "row"}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{item.name || "-"}</td>
                    <td>{item.base_uom || "-"}</td>
                    <td>{item.available ?? 0}</td>
                    <td>{item.min_stock ?? 0}</td>
                    <td>{item.reorder_qty ?? 0}</td>
                    <td>{fmtMoney(item.stock_value)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
        <Actions style={{ marginTop: 10 }}>
          <PrimaryBtn type="button" onClick={exportPdf}>
            PDF
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={() => printThermal(reportRef.current, "inventory_low_stock")}>
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={exportCsv}>
            CSV
          </PrimaryBtn>
        </Actions>
      </Card>
    </Page>
  );
};

export default InventoryLowStockReportPage;

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;

const PillRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Pill = styled.span`
  background: #eef2ff;
  color: ${COLOR.text};
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 8px 10px;
  font-weight: 800;
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;

const Sub = styled.div`
  color: ${COLOR.muted};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  align-items: center;
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    font-weight: 800;
  }

  @media (max-width: 768px) {
    min-width: 620px;
    th,
    td {
      padding: 8px 10px;
      font-size: 12px;
      white-space: nowrap;
    }
  }
`;

const Center = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 24px;
`;
